// lastShopId - remembers the most recent shop the user added items to.
//
// Used by:
//   - the header cart badge (so it knows which cart to query when the user
//     is not on a shop route)
//   - the cross-shop warning banner (when the user adds to shop B while
//     shop A's cart is still active server-side)
//
// localStorage is wrapped in try/catch because private mode, quota exhaustion,
// and SSR all throw on access. In those cases the cart still works for the
// current page; only the cross-tab/cross-page memory degrades.
//
// REACTIVE: set()/clear() notify in-process subscribers, and we listen for the
// cross-tab `storage` event, so React components reading via useLastShopId()
// re-render the moment the value changes - without this the header cart badge
// stayed stale until a page refresh after the first add of a session.

const KEY = "monpanier:lastShopId";

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

// Cross-tab updates: another tab writing localStorage fires `storage` here.
// (Same-tab writes don't fire `storage`, which is why set()/clear() notify
// explicitly below.)
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === KEY || e.key === null) notify();
  });
}

export const lastShopId = {
  get(): string | null {
    try {
      return localStorage.getItem(KEY);
    } catch {
      return null;
    }
  },
  set(shopId: string): void {
    try {
      localStorage.setItem(KEY, shopId);
    } catch {
      // localStorage unavailable (private mode, quota, etc.) - silently
      // accept; the cart still works for the current page but the
      // header badge won't persist across tabs.
    }
    notify();
  },
  clear(): void {
    try {
      localStorage.removeItem(KEY);
    } catch {
      // ignore
    }
    notify();
  },
  /** Subscribe to changes (for useSyncExternalStore). Returns an unsubscribe. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
